/**
 * Tela de entrada (AIH-26).
 *
 * Duas responsabilidades: traduzir o erro que o Entra devolveu e levar adiante o
 * destino que a pessoa tentou abrir antes de logar.
 *
 * ## Por que ela é mais delicada que as outras
 *
 * É a página onde a pessoa cai **quando alguma coisa quebrou**. Se o pacote não
 * carregar, ela não vê nem a mensagem de erro nem o botão de tentar de novo —
 * fica sem saída. Por isso aqui não se busca dado, não se espera resposta e não
 * há estado assíncrono: tudo o que ela mostra sai da própria URL, de forma
 * síncrona, na primeira renderização.
 *
 * Nenhum `onclick` no HTML e nenhum `style=` inline: a CSP tem `script-src
 * 'self'` e `style-src 'self'`, sem `unsafe-inline`. Em React o `onClick` é
 * seguro — vira listener por delegação, não vira atributo. Já o `style={{...}}`
 * gera atributo `style=` de verdade e **é bloqueado**. Ver o teste em
 * backend/tests/frontend.test.js.
 */
import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta.js';

/* ── Erros ──────────────────────────────────────────────────────────────── */

/**
 * O que o Entra devolve, traduzido para o que a pessoa precisa FAZER.
 *
 * Mensagem de erro que só descreve o problema deixa quem lê parado. Cada entrada
 * aqui termina apontando uma saída — tentar de novo, procurar o gestor, falar
 * com a TI.
 */
const SIGNIN_ERRORS = {
  access_denied: {
    title: 'Acesso não autorizado',
    text:
      'Sua conta não tem permissão para entrar no AI Hub, ou o consentimento foi negado. ' +
      'Se você deveria ter acesso, peça a liberação ao seu gestor.',
  },
  consent_required: {
    title: 'Consentimento pendente',
    text:
      'O AI Hub ainda não recebeu a autorização do administrador do Microsoft 365. ' +
      'Avise a equipe de Inovação e IA.',
  },
  interaction_required: {
    title: 'A Microsoft pediu uma confirmação a mais',
    text:
      'Pode ser verificação em duas etapas ou uma política de segurança do dispositivo. ' +
      'Tente entrar novamente e conclua o que for pedido.',
  },
  login_required: {
    title: 'Sessão encerrada na Microsoft',
    text: 'Sua sessão expirou do lado da Microsoft. Entre novamente.',
  },
  unauthorized_client: {
    title: 'Configuração da aplicação',
    text:
      'O registro do AI Hub no Entra ID está incompleto. Isso é problema nosso, não seu — ' +
      'avise a equipe de Inovação e IA.',
  },
  invalid_request: {
    title: 'Pedido de login inválido',
    text: 'Algo se perdeu no caminho. Tente entrar novamente a partir desta tela.',
  },
  server_error: {
    title: 'A Microsoft está instável',
    text: 'O serviço de autenticação falhou. Aguarde um instante e tente de novo.',
  },
  temporarily_unavailable: {
    title: 'Serviço temporariamente indisponível',
    text: 'A Microsoft está fora do ar no momento. Tente novamente em alguns minutos.',
  },
  sessao: {
    title: 'Sua sessão expirou durante o login',
    text: 'O tempo entre abrir esta tela e concluir na Microsoft passou do limite. Comece de novo.',
  },
};

const DEFAULT_SIGNIN_ERROR = {
  title: 'Não foi possível entrar',
  text: 'Tente novamente. Se continuar, avise a equipe de Inovação e IA.',
};

/* ── Destino de retorno ─────────────────────────────────────────────────── */

/**
 * Mesmas regras do `caminhoSeguroDeRetorno` do servidor.
 *
 * A validação aqui é conveniência, não defesa: quem controla o navegador
 * controla este código. Quem realmente barra a redireção aberta é o servidor,
 * que valida de novo antes de usar o valor.
 */
function safePath(value) {
  if (typeof value !== 'string' || value.length === 0 || value.length > 512) return null;
  if (value[0] !== '/') return null;
  if (value[1] === '/' || value[1] === '\\') return null;
  // eslint-disable-next-line no-control-regex -- é exatamente o que se quer barrar
  if (/[\u0000-\u001f\u007f]/.test(value)) return null;
  return value;
}

/** Acrescenta `returnTo` ao destino, quando há um destino válido. */
function withReturnTo(href, target) {
  if (!target) return href;
  const url = new URL(href, window.location.origin);
  url.searchParams.set('returnTo', target);
  return url.pathname + url.search;
}

/* ── Página ─────────────────────────────────────────────────────────────── */

export function SignIn() {
  usePageMeta('Entrar · AI Hub Iguatemi', 'signin');

  // Pelo roteador, e nao por window.location: assim a tela reage se a query
  // mudar sem recarregar a pagina — que e o que acontece numa aplicacao de
  // pagina unica.
  const [params] = useSearchParams();

  const signedOut = params.get('saiu') === '1';

  const { error, target } = useMemo(() => {
    const code = params.get('erro');

    // Object.hasOwn: sem ele, ?erro=constructor acha a chave herdada de
    // Object.prototype, e a tela imprime "undefined" para o usuário.
    const found = code
      ? Object.hasOwn(SIGNIN_ERRORS, code)
        ? SIGNIN_ERRORS[code]
        : DEFAULT_SIGNIN_ERROR
      : null;

    return { error: found, target: safePath(params.get('returnTo')) };
  }, [params]);

  return (
    <>
      {/* Faixa dourada e ornamentos: decoração pura, escondida do leitor de tela. */}
      <div className="top-stripe" aria-hidden="true" />
      <div className="dots" aria-hidden="true" />
      <div className="halos" aria-hidden="true" />

      <main className="stage">
        <img className="brand-mark" src="/marca/iguatemi_logotipo_white.png" alt="Iguatemi" />

        <p className="eyebrow">Central de Inteligência Artificial</p>
        <h1 className="headline">
          Bem-vindo ao <span className="shine">AI Hub</span>
        </h1>
        <p className="subhead">
          Acesse o catálogo de agentes e as plataformas de IA homologadas pela companhia.
        </p>

        {/* role="alert" faz o leitor de tela anunciar sozinho quando aparece. */}
        <div className="alert" id="signin-alert" role="alert" hidden={!error}>
          {error && (
            <>
              <strong>{error.title}</strong>
              {error.text}
            </>
          )}
        </div>

        {/* Depois de sair, a sessao do AI Hub acabou mas a da Microsoft
            continua no navegador — entrar de novo nao vai pedir senha. Dizer
            isso evita a conclusao de que o Sair nao funcionou. */}
        {signedOut && !error && (
          <div className="alert notice" role="status">
            <strong>Você saiu do AI Hub.</strong>
            Sua conta Microsoft segue conectada neste navegador: entrar de novo não vai pedir senha.
          </div>
        )}

        <a className="signin-button" id="signin-action" href={withReturnTo('/auth/login', target)}>
          <svg className="ms-logo" viewBox="0 0 21 21" aria-hidden="true" focusable="false">
            <rect x="1" y="1" width="9" height="9" fill="#F25022" />
            <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
            <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
            <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
          </svg>
          <span>Entrar com a conta Microsoft</span>
        </a>
      </main>

      <footer className="signin-footer">
        <p className="signature">Iguatemi S.A. · Inovação e IA</p>
        <p className="legal-links">
          <Link to="/termos">Condições de uso</Link>
          <span aria-hidden="true">·</span>
          <Link to="/privacidade">Tratamento de dados</Link>
        </p>
      </footer>
    </>
  );
}
